import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useOrbitAuth } from '../context/OrbitAuthContext';
import { useLanguage } from '../context/LanguageContext';
import { MapPin, Eye, EyeOff } from 'lucide-react';

const LanguageToggle = ({ language, toggleLanguage }) => (
  <div className="absolute top-6 right-6 z-10 flex bg-white/80 backdrop-blur-sm border border-[hsl(40,20%,88%)] rounded-full overflow-hidden text-xs shadow-sm" data-testid="auth-lang-toggle">
    <button onClick={() => language !== 'fr' && toggleLanguage()}
      className={`px-3 py-1.5 font-semibold transition-colors ${language === 'fr' ? 'bg-[var(--ls-terracotta)] text-white' : 'text-[var(--ls-text-muted)]'}`}>FR</button>
    <button onClick={() => language !== 'en' && toggleLanguage()}
      className={`px-3 py-1.5 font-semibold transition-colors ${language === 'en' ? 'bg-[var(--ls-terracotta)] text-white' : 'text-[var(--ls-text-muted)]'}`}>EN</button>
  </div>
);

const OrbitLoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login, error } = useOrbitAuth();
  const { language, toggleLanguage } = useLanguage();
  const navigate = useNavigate();
  const t = (fr, en) => language === 'fr' ? fr : en;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      navigate('/dashboard');
    } catch (e) { console.error('Login error:', e); } finally { setLoading(false); }
  };

  const handleGoogleLogin = () => {
    window.location.href = `${process.env.REACT_APP_BACKEND_URL}/api/orbit/auth/google/login`;
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left — Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-[var(--ls-sand)] relative">
        <LanguageToggle language={language} toggleLanguage={toggleLanguage} />
        <div className="w-full max-w-md space-y-8">
          {/* Logo */}
          <div className="text-center lg:text-left">
            <Link to="/" className="inline-flex items-center gap-2 mb-8">
              <MapPin className="w-8 h-8 text-[var(--ls-terracotta)]" />
              <span className="font-heading text-2xl font-bold text-[var(--ls-text)]">
                Local<span className="text-[var(--ls-terracotta)]">Site</span>.Club
              </span>
            </Link>
            <h1 className="font-heading text-3xl sm:text-4xl font-bold text-[var(--ls-text)] tracking-tight">
              {t('Bon retour !', 'Welcome back!')}
            </h1>
            <p className="mt-2 text-[var(--ls-text-muted)]">
              {t('Connectez-vous a votre espace membre', 'Sign in to your member space')}
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm" data-testid="login-error">
                {error}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-[var(--ls-text)] mb-1.5">{t('Courriel', 'Email')}</label>
              <input
                data-testid="orbit-login-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full px-4 py-3 rounded-xl border border-[hsl(40,20%,88%)] bg-white text-[var(--ls-text)] placeholder:text-[var(--ls-text-muted)]/50 focus:outline-none focus:ring-2 focus:ring-[var(--ls-terracotta)]/30 focus:border-[var(--ls-terracotta)] transition-all"
                placeholder={t('votre@courriel.com', 'your@email.com')}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-[var(--ls-text)] mb-1.5">{t('Mot de passe', 'Password')}</label>
              <div className="relative">
                <input
                  data-testid="orbit-login-password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="w-full px-4 py-3 rounded-xl border border-[hsl(40,20%,88%)] bg-white text-[var(--ls-text)] placeholder:text-[var(--ls-text-muted)]/50 focus:outline-none focus:ring-2 focus:ring-[var(--ls-terracotta)]/30 focus:border-[var(--ls-terracotta)] transition-all pr-12"
                  placeholder="********"
                />
                <button type="button" onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--ls-text-muted)] hover:text-[var(--ls-text)]">
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <button
              data-testid="orbit-login-submit"
              type="submit"
              disabled={loading}
              className="w-full py-3.5 rounded-full bg-[var(--ls-terracotta)] text-white font-semibold hover:bg-[var(--ls-terracotta-dark)] transition-colors disabled:opacity-50 shadow-lg shadow-[var(--ls-terracotta)]/20"
            >
              {loading ? t('Connexion...', 'Signing in...') : t('Se connecter', 'Sign in')}
            </button>

            <div className="relative flex items-center gap-4 py-2">
              <div className="flex-1 h-px bg-[hsl(40,20%,88%)]"></div>
              <span className="text-xs text-[var(--ls-text-muted)] uppercase tracking-wider">{t('ou', 'or')}</span>
              <div className="flex-1 h-px bg-[hsl(40,20%,88%)]"></div>
            </div>

            <button
              type="button"
              onClick={handleGoogleLogin}
              data-testid="orbit-login-google"
              className="w-full py-3.5 rounded-full border border-[hsl(40,20%,88%)] bg-white text-[var(--ls-text)] font-medium hover:bg-[var(--ls-sand-dark)] transition-colors flex items-center justify-center gap-3"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
              {t('Continuer avec Google', 'Continue with Google')}
            </button>
          </form>

          {/* Footer links */}
          <div className="text-center space-y-3 pt-4">
            <p className="text-sm text-[var(--ls-text-muted)]">
              {t("Pas encore membre ?", "Not a member yet?")}
              <Link to="/register" className="text-[var(--ls-terracotta)] hover:text-[var(--ls-terracotta-dark)] ml-1 font-semibold">
                {t("S'inscrire", 'Sign up')}
              </Link>
            </p>
            <Link to="/" className="text-xs text-[var(--ls-text-muted)] hover:text-[var(--ls-text)] transition-colors">
              {t('Retour au repertoire', 'Back to directory')}
            </Link>
          </div>
        </div>
      </div>

      {/* Right — Visual */}
      <div className="hidden lg:flex w-1/2 relative overflow-hidden">
        <img
          src="https://static.prod-images.emergentagent.com/jobs/987d762f-6280-4d2f-beee-1ba5be49cef1/images/cf3ce17456f3599f7b66774ca5e98ed2d49a95335c88f4cffdf5e6f84b99a28f.png"
          alt="Local artisan market"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[var(--ls-bark)]/80 via-[var(--ls-bark)]/30 to-transparent"></div>
        <div className="relative z-10 flex flex-col justify-end p-12 text-white">
          <h2 className="font-heading text-4xl font-bold mb-3">
            {t("Soutenons nos artisans d'ici", "Support our local artisans")}
          </h2>
          <p className="text-white/80 text-lg max-w-md">
            {t(
              "Rejoignez le mouvement LocalSite.Club et decouvrez les tresors de votre quartier.",
              "Join the LocalSite.Club movement and discover the treasures of your neighborhood."
            )}
          </p>
        </div>
      </div>
    </div>
  );
};

export default OrbitLoginPage;
